import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  type Timestamp as FirestoreTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Shipment, Market } from '../types';

// Date range presets
export type DateRangePreset =
  | '6h' | '12h' | '24h' | '48h'
  | 'today' | 'yesterday'
  | 'this_week' | 'last_week' | 'last_2_weeks' | 'last_3_weeks'
  | '7d' | '14d' | '30d'
  | 'custom';

interface DateRange {
  start: Date;
  end: Date;
}

// Convert Firestore timestamps to Dates
function convertTimestamps(data: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    if (value && typeof value === 'object') {
      if ('toDate' in value && typeof (value as FirestoreTimestamp).toDate === 'function') {
        result[key] = (value as FirestoreTimestamp).toDate();
      } else if (Array.isArray(value)) {
        result[key] = value.map((item) =>
          typeof item === 'object' ? convertTimestamps(item as Record<string, unknown>) : item
        );
      } else {
        result[key] = convertTimestamps(value as Record<string, unknown>);
      }
    } else {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Get start of week (Monday) for a given date
 */
function getStartOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Calculate date range from preset
 */
function getDateRangeFromPreset(preset: DateRangePreset, customRange?: DateRange): DateRange {
  const now = new Date();

  switch (preset) {
    // Hour-based presets
    case '6h': {
      const start = new Date(now.getTime() - 6 * 60 * 60 * 1000);
      return { start, end: now };
    }
    case '12h': {
      const start = new Date(now.getTime() - 12 * 60 * 60 * 1000);
      return { start, end: now };
    }
    case '24h': {
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return { start, end: now };
    }
    case '48h': {
      const start = new Date(now.getTime() - 48 * 60 * 60 * 1000);
      return { start, end: now };
    }

    // Day-based presets
    case 'today': {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case 'yesterday': {
      const start = new Date(now);
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }

    // Week-based presets
    case 'this_week': {
      const start = getStartOfWeek(now);
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case 'last_week': {
      const thisWeekStart = getStartOfWeek(now);
      const start = new Date(thisWeekStart);
      start.setDate(start.getDate() - 7);
      const end = new Date(thisWeekStart);
      end.setMilliseconds(-1); // End of previous week (Sunday 23:59:59.999)
      return { start, end };
    }
    case 'last_2_weeks': {
      const start = new Date(now);
      start.setDate(start.getDate() - 14);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case 'last_3_weeks': {
      const start = new Date(now);
      start.setDate(start.getDate() - 21);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }

    // Legacy day-count presets
    case '7d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case '14d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 14);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case '30d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }

    case 'custom':
      if (customRange) {
        return customRange;
      }
      // Fallback to today if no custom range provided
      return getDateRangeFromPreset('today');

    default:
      return getDateRangeFromPreset('today');
  }
}

/**
 * Hook for fetching delivered shipments with date range filtering.
 * Uses real-time Firestore subscription for live updates.
 */
export function useDeliveredShipments(
  market: Market | 'all' = 'all',
  preset: DateRangePreset = '7d',
  customRange?: DateRange
) {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate date range from preset
  const dateRange = useMemo(
    () => getDateRangeFromPreset(preset, customRange),
    [preset, customRange?.start?.getTime(), customRange?.end?.getTime()]
  );

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Build query for delivered shipments within date range
    // Firestore requires equality filters before range filters
    let q;

    if (market !== 'all') {
      // With market filter
      q = query(
        collection(db, 'shipments'),
        where('is_delivered', '==', true),
        where('market', '==', market),
        where('delivered_at', '>=', Timestamp.fromDate(dateRange.start)),
        where('delivered_at', '<=', Timestamp.fromDate(dateRange.end)),
        orderBy('delivered_at', 'desc')
      );
    } else {
      // All markets
      q = query(
        collection(db, 'shipments'),
        where('is_delivered', '==', true),
        where('delivered_at', '>=', Timestamp.fromDate(dateRange.start)),
        where('delivered_at', '<=', Timestamp.fromDate(dateRange.end)),
        orderBy('delivered_at', 'desc')
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...convertTimestamps(doc.data()),
        })) as Shipment[];
        setShipments(data);
        setLoading(false);
      },
      (err) => {
        console.error('Firestore error (delivered):', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [market, dateRange.start.getTime(), dateRange.end.getTime()]);

  return { shipments, loading, error, dateRange };
}

export type { DateRange };
